import { UserContext } from 'context/userContext';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  CasinoFavorite,
  getCasinoFavorites,
  setCasinoFavorite,
} from 'services/api/gameApiService';
import { ToastMessagesIDs } from 'types/enums';

/** The logged-in player's starred games of one lobby, newest first, and a
 *  star toggle. The list lives on the server with the account. */
export const useCasinoFavorites = (kind: CasinoFavorite['kind']) => {
  const { loggedUser } = useContext(UserContext);
  const { t } = useTranslation();
  const [all, setAll] = useState<CasinoFavorite[]>([]);
  const playerId = loggedUser?.id;

  useEffect(() => {
    let live = true;
    setAll([]);
    if (!playerId) return;
    getCasinoFavorites()
      .then((favs) => live && setAll(favs))
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [playerId]);

  const uuids = useMemo(
    () => all.filter((f) => f.kind === kind).map((f) => f.uuid),
    [all, kind]
  );
  const starred = useMemo(() => new Set(uuids), [uuids]);

  const toggle = useCallback(
    async (uuid: string) => {
      if (!playerId) {
        toast(t('must_be_logged_in'), {
          toastId: ToastMessagesIDs.CASINO_GAMES_ACCESS_ERROR,
        });
        return;
      }
      const favorite = !starred.has(uuid);
      const before = all;
      // Shown at once; the server's answer then settles the list.
      setAll(
        favorite ? [{ uuid, kind }, ...all] : all.filter((f) => f.uuid !== uuid)
      );
      try {
        setAll(await setCasinoFavorite(uuid, kind, favorite));
      } catch {
        setAll(before);
      }
    },
    [playerId, starred, all, kind, t]
  );

  return { uuids, isFavorite: (uuid: string) => starred.has(uuid), toggle };
};
